/**
 * Escrow & Refunds Policy (P20250925-103)
 *
 * SEO-ready, indexable, with ToC and Last Updated stamp.
 */

import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export default function LegalEscrowRefunds() {
  const lastUpdated = '2025-09-30';

  return (
    <>
      <Helmet>
        <title>Escrow & Refunds Policy - Attain Domains</title>
        <meta
          name="description"
          content="Attain Domains Escrow & Refunds: How our trusted payment provider protects buyers, fee coverage, transfer timeline, and refund/cancellation policies for purchases and leases."
        />
        <link rel="canonical" href="https://attaindomains.com/legal/escrow-and-refunds" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <h1 className="text-4xl font-bold mb-2 text-center">Escrow & Refunds Policy</h1>
          <p className="text-sm text-muted-foreground text-center mb-8">
            Last updated: {lastUpdated}
          </p>

          {/* Table of Contents */}
          <nav className="bg-muted/30 rounded-lg p-6 mb-8" aria-label="Table of contents">
            <h2 className="text-lg font-semibold mb-3">Contents</h2>
            <ul className="space-y-1 text-sm">
              <li>
                <a href="#escrow-overview" className="text-primary hover:underline">
                  1. Escrow Overview
                </a>
              </li>
              <li>
                <a href="#fees" className="text-primary hover:underline">
                  2. Escrow Fees
                </a>
              </li>
              <li>
                <a href="#transfer-timeline" className="text-primary hover:underline">
                  3. Transfer Timeline
                </a>
              </li>
              <li>
                <a href="#refunds-purchases" className="text-primary hover:underline">
                  4. Refunds (Purchases)
                </a>
              </li>
              <li>
                <a href="#refunds-leasing" className="text-primary hover:underline">
                  5. Cancellations (Leasing)
                </a>
              </li>
              <li>
                <a href="#contact" className="text-primary hover:underline">
                  6. Contact
                </a>
              </li>
            </ul>
          </nav>

          <div className="prose prose-lg max-w-none space-y-8">
            {/* Escrow Overview */}
            <section
              id="escrow-overview"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">1. Escrow Overview</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>
                    All Buy-It-Now (BIN) purchases are protected by our trusted payment provider.
                  </strong>{' '}
                  Escrow acts as a neutral third party:
                </p>
                <ol className="list-decimal list-inside space-y-2">
                  <li>
                    <strong>Buyer pays our trusted payment provider:</strong> Funds are held
                    securely (not released to seller yet).
                  </li>
                  <li>
                    <strong>Seller initiates transfer:</strong> Domain is unlocked and transferred
                    to buyer's registrar.
                  </li>
                  <li>
                    <strong>Buyer inspects:</strong> Typically 5-7 business days to verify domain
                    receipt.
                  </li>
                  <li>
                    <strong>Funds released:</strong> Once buyer approves (or inspection period
                    expires without dispute), Escrow releases funds to seller.
                  </li>
                </ol>
                <p>
                  <strong>Why Escrow?</strong> Protects both parties: buyer gets domain before funds
                  are released; seller gets paid once transfer is verified.
                </p>
              </div>
            </section>

            {/* Fees */}
            <section id="fees" className="bg-card rounded-lg p-6 border border-border scroll-mt-20">
              <h2 className="text-2xl font-semibold mb-4 text-foreground">2. Escrow Fees</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Buyer pays our trusted payment provider fees</strong> (standard industry
                  practice):
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Rate:</strong> Typically 3.25% of transaction value, minimum $25 USD.
                  </li>
                  <li>
                    <strong>Example:</strong> $1,000 domain = $32.50 Escrow fee; $500 domain = $25
                    fee (minimum applies).
                  </li>
                  <li>
                    <strong>High-value:</strong> Fees may be lower for transactions &gt;$5,000
                    (consult our trusted payment provider fee schedule).
                  </li>
                </ul>
                <p>
                  <strong>Attain Domains does not cover Escrow fees by default.</strong> The buyer
                  is responsible unless explicitly stated otherwise in a promotional offer.
                </p>
                <p>
                  <em>
                    Note: our trusted payment provider may offer occasional promotions (e.g.,
                    "seller pays fees"). Check your transaction details before confirming.
                  </em>
                </p>
              </div>
            </section>

            {/* Transfer Timeline */}
            <section
              id="transfer-timeline"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">3. Transfer Timeline</h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Typical timeline:</strong>
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Payment verification:</strong> 1-2 business days (wire transfers may
                    take longer).
                  </li>
                  <li>
                    <strong>Transfer initiation:</strong> Seller unlocks domain and provides auth
                    code within 1 business day of Escrow confirmation.
                  </li>
                  <li>
                    <strong>Domain transfer:</strong> 5-7 business days (varies by registrar; .com
                    typically faster than some ccTLDs).
                  </li>
                  <li>
                    <strong>Inspection period:</strong> 5 days (buyer can dispute if domain doesn't
                    match listing).
                  </li>
                </ul>
                <p>
                  <strong>Total:</strong> Expect <strong>10-14 business days</strong> from payment
                  to full ownership (may be faster for .com domains with expedited registrar
                  approvals).
                </p>
                <p>
                  <em>
                    Delays? Contact{' '}
                    <Link to="/support/contact" className="text-primary hover:underline">
                      Support
                    </Link>{' '}
                    or our trusted payment provider directly via your transaction dashboard.
                  </em>
                </p>
              </div>
            </section>

            {/* Refunds (Purchases) */}
            <section
              id="refunds-purchases"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                4. Refunds (Purchases)
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Sales are final once Escrow releases funds</strong> (typically after buyer
                  approves transfer). However, refunds are available in these cases:
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Domain not as described:</strong> If the domain differs materially from
                    the listing (e.g., trademark dispute, locked status), open an Escrow dispute
                    during the inspection period.
                  </li>
                  <li>
                    <strong>Failed transfer:</strong> If seller cannot deliver the domain (e.g., no
                    longer owns it), Escrow refunds 100% + fees.
                  </li>
                  <li>
                    <strong>Buyer cancels before transfer:</strong> If you cancel <em>before</em>{' '}
                    the seller initiates transfer, Escrow refunds your payment minus a small
                    processing fee (~$25).
                  </li>
                </ul>
                <p>
                  <strong>No refunds after approval:</strong> Once you approve the domain receipt in
                  Escrow, the sale is final. Inspect carefully during the 5-day window!
                </p>
                <p>
                  <strong>Disputes:</strong> All refund disputes are handled by our trusted payment
                  provider per their{' '}
                  <a
                    href="https://www.our payment provider/why-escrowcom/how-does-escrow-work"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    terms
                  </a>
                  . Contact their support if you believe you qualify for a refund.
                </p>
              </div>
            </section>

            {/* Cancellations (Leasing) */}
            <section
              id="refunds-leasing"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">
                5. Cancellations (Leasing)
              </h2>
              <div className="space-y-3 text-muted-foreground">
                <p>
                  <strong>Lease-to-Own cancellations:</strong> Request cancellation via your{' '}
                  <Link to="/account" className="text-primary hover:underline">
                    Account page
                  </Link>{' '}
                  (Admin must approve).
                </p>
                <ul className="list-disc list-inside space-y-2">
                  <li>
                    <strong>Early termination (before 50% paid):</strong> No refund; payments cover
                    hosting/use fees.
                  </li>
                  <li>
                    <strong>Late-stage termination (after 50% paid):</strong> Prorated refund of 25%
                    of remaining balance (minus admin fees).
                  </li>
                  <li>
                    <strong>Completed leases:</strong> Domain transfers to your ownership; no
                    cancellations after final payment.
                  </li>
                </ul>
                <p>
                  <strong>Example:</strong> 12-month lease at $100/mo ($1,200 total). You pay 6
                  months ($600), then cancel:
                </p>
                <ul className="list-disc list-inside space-y-1 text-sm">
                  <li>Remaining balance: $600</li>
                  <li>Prorated refund: $600 × 25% = $150</li>
                  <li>Admin fee: $25</li>
                  <li>
                    <strong>Net refund: $125</strong>
                  </li>
                </ul>
                <p>
                  <em>
                    Note: Leasing is currently in beta. Refund policy may change; existing leases
                    are grandfathered.
                  </em>
                </p>
              </div>
            </section>

            {/* Contact */}
            <section
              id="contact"
              className="bg-card rounded-lg p-6 border border-border scroll-mt-20"
            >
              <h2 className="text-2xl font-semibold mb-4 text-foreground">6. Contact</h2>
              <div className="space-y-2 text-muted-foreground">
                <p>Questions about Escrow or refunds?</p>
                <p>
                  <strong>Attain Domains Support:</strong>{' '}
                  <Link to="/support/contact" className="text-primary hover:underline">
                    Contact form
                  </Link>
                </p>
                <p>
                  <strong>our trusted payment provider Support:</strong>{' '}
                  <a
                    href="https://www.our payment provider/support"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline"
                  >
                    our payment provider/support
                  </a>
                </p>
                <p>
                  <strong>Email:</strong> support@attaindomains.com
                </p>
              </div>
            </section>
          </div>
        </div>
      </div>
    </>
  );
}
